import type { InfiniteData } from '@tanstack/react-query';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GetMainOriginalsListResponse } from 'src/store/server/main/new/main.dto';
import { USE_MAIN_QUERY_KEY } from 'src/store/server/main/new/main.query';
import type {
  GetMintingInitRequest,
  GetMintingInitResponse,
  MintingName,
  OriginalsName,
} from 'src/store/server/originals/originals.dto';
import {
  type GetOriginalsInitRequest,
  type GetOriginalsInitResponse,
  type GetOriginalsListRequest,
  type GetOriginalsListResponse,
  type SetOriginalsFavorRequest,
} from 'src/store/server/originals/originals.dto';
import { originalsService } from 'src/store/server/originals/originals.service';
import type { APICommonResponse } from 'src/types/common.types';
import { momentTimezone } from 'src/utils/time';

export const USE_ORIGINALS_QUERY_KEY = {
  getOriginalsInit: 'get-originals-init',
  getMintingInit: 'get-minting-init',
  getOriginalsList: 'get-originals-list',
};

// 오리지널스 초기 정보 조회
interface GetOriginalsInitParams {
  originalsNameList?: GetOriginalsInitRequest['originals_name_list'];
}

export const useGetOriginalsInitQuery = (params?: GetOriginalsInitParams) => {
  return useQuery(
    [USE_ORIGINALS_QUERY_KEY.getOriginalsInit, params],
    () =>
      originalsService.getOriginalsInit({
        originals_name_list: params?.originalsNameList,
      }),
    {
      select: (res: APICommonResponse<GetOriginalsInitResponse>) => {
        const { data } = res.data;
        return Object.entries(data).reduce(
          (acc, [key, value]) => {
            acc[key as OriginalsName] = {
              originalsIdx: value.originals_idx,
              startDateTime: value.start_dttm,
              endDateTime: value.end_dttm,
            };
            return acc;
          },
          {} as Record<OriginalsName, { originalsIdx: number; startDateTime: number; endDateTime: number }>,
        );
      },
    },
  );
};

// 민팅 초기 정보 조회
interface GetMintingInitParams {
  mintingNameList?: GetMintingInitRequest['minting_name_list'];
}

export const useGetMintingInitQuery = (params?: GetMintingInitParams) => {
  return useQuery(
    [USE_ORIGINALS_QUERY_KEY.getMintingInit, params],
    () =>
      originalsService.getMintingInit({
        minting_name_list: params?.mintingNameList,
      }),
    {
      select: (res: APICommonResponse<GetMintingInitResponse>) => {
        const { data } = res.data;
        return Object.entries(data).reduce(
          (acc, [key, value]) => {
            acc[key as MintingName] = {
              mintingIdx: value.minting_idx,
              startDateTime: value.start_dttm,
              endDateTime: value.end_dttm,
            };
            return acc;
          },
          {} as Record<MintingName, { mintingIdx: number; startDateTime: number; endDateTime: number }>,
        );
      },
    },
  );
};

// 오리지널스 목록 조회
interface GetOriginalsListParams {
  cursor: GetOriginalsListRequest['cursor'];
  size: GetOriginalsListRequest['size'];
  status?: GetOriginalsListRequest['status'];
}

export const useGetOriginalsListInfiniteQuery = (params: GetOriginalsListParams) => {
  return useInfiniteQuery(
    [USE_ORIGINALS_QUERY_KEY.getOriginalsList, params],
    ({ pageParam = params.cursor }) =>
      originalsService.getOriginalsList({
        cursor: pageParam, // pageParam 사용
        size: params.size,
        status: params.status,
      }),
    {
      getNextPageParam: (lastPage) => lastPage.data.data.next_cursor,
      select: (data) => {
        const { pages, pageParams } = data;
        return {
          pages: pages.map((page) => {
            const { data } = page.data;
            return data.rows.map((item) => ({
              idx: item.idx,
              name: item.name,
              title: item.title,
              subTitle: item.sub_title,
              startDateTime: item.start_dttm,
              endDateTime: item.end_dttm,
              image: item.image,
              pickName: item.pick_name,
              pickImage: item.pick_image,
              linkUrl: item.link_url,
              blankYN: item.blank_yn,
              myFavorite: item.my_favor,
            }));
          }),
          pageParams: {
            ...pageParams,
          },
        };
      },
    },
  );
};

// 오리지널스 좋아요
interface SetOriginalsFavorParams {
  originalsIdx: SetOriginalsFavorRequest['originals_idx'];
  favorYN: SetOriginalsFavorRequest['favor_yn'];
}

export const useSetOriginalsFavorMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: APICommonResponse<null>) => void;
  onError?: (e) => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    (params: SetOriginalsFavorParams) =>
      originalsService.setOriginalsFavor({ originals_idx: params.originalsIdx, favor_yn: params.favorYN }),
    {
      // 1. 낙관적 업데이트 처리
      onMutate: async ({ originalsIdx, favorYN }) => {
        // 1-1. 현재 활성화된 모든 infinite queries 찾기
        const activeOriginalsQueries = queryClient.getQueryCache().findAll([USE_ORIGINALS_QUERY_KEY.getOriginalsList]);

        // 1-2. 진행 중인 쿼리들 refetch 취소
        await Promise.all([
          queryClient.cancelQueries([USE_MAIN_QUERY_KEY.getMainOriginalsList]),
          ...activeOriginalsQueries.map((query) => queryClient.cancelQueries(query.queryKey)),
        ]);

        // 1-3. 현재 데이터 백업
        const previousMainData = queryClient.getQueryData([USE_MAIN_QUERY_KEY.getMainOriginalsList]);
        const previousOriginalsData = activeOriginalsQueries.map((query) => ({
          queryKey: query.queryKey,
          data: queryClient.getQueryData(query.queryKey),
        }));

        // 1-4. 캐시 데이터 즉시 업데이트
        // 메인 오리지널스 리스트 업데이트
        queryClient.setQueryData<APICommonResponse<GetMainOriginalsListResponse>>(
          [USE_MAIN_QUERY_KEY.getMainOriginalsList],
          (oldData) => {
            if (!oldData?.data?.data) return oldData;

            return {
              ...oldData,
              data: {
                ...oldData.data,
                data: oldData.data.data.map((item) =>
                  item.idx === originalsIdx
                    ? {
                        ...item,
                        my_favor: favorYN,
                        favor_dttm: favorYN === 'Y' ? momentTimezone().unix() : null,
                      }
                    : item,
                ),
              },
            };
          },
        );

        // 오리지널스 무한 스크롤 리스트 업데이트
        activeOriginalsQueries.forEach((query) => {
          queryClient.setQueryData<InfiniteData<APICommonResponse<GetOriginalsListResponse>>>(
            query.queryKey,
            (oldData) => {
              if (!oldData?.pages) return oldData;
              return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                  ...page,
                  data: {
                    ...page.data,
                    data: {
                      ...page.data.data,
                      rows: page.data.data.rows.map((item) =>
                        item.idx === originalsIdx ? { ...item, my_favor: favorYN } : item,
                      ),
                    },
                  },
                })),
              };
            },
          );
        });

        // 1-5. 롤백을 위해 이전 데이터 반환
        return { previousMainData, previousOriginalsData };
      },
      // 2-a. API 호출 성공 시
      onSuccess: (res) => {
        onSuccess && onSuccess(res);
      },

      // 2-b. API 호출 실패 시
      onError: (error, _, context) => {
        // 에러 시 두 쿼리 모두 롤백
        if (context?.previousMainData) {
          queryClient.setQueryData([USE_MAIN_QUERY_KEY.getMainOriginalsList], context.previousMainData);
        }
        if (context?.previousOriginalsData) {
          queryClient.setQueryData([USE_ORIGINALS_QUERY_KEY.getOriginalsList], context.previousOriginalsData);
        }
        onError && onError(error);
      },
      // 3. 성공/실패 상관없이 완료 시
      onSettled: () => {
        // 서버 데이터와 동기화를 위한 캐시 무효화
        queryClient.invalidateQueries({
          queryKey: [USE_MAIN_QUERY_KEY.getMainOriginalsList],
        });
        queryClient.invalidateQueries({
          queryKey: [USE_ORIGINALS_QUERY_KEY.getOriginalsList],
        });
      },
    },
  );
};
